// src/screens/applications/MyApplicationsScreen.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, where, getDocs, doc, getDoc, orderBy, limit } from "firebase/firestore";
import { auth, db } from "../../firebase";
import Sidebar from "../../components/dashboard/Sidebar";
import { generateReceipt } from "../../utils/generateReceipt";

const STATUS_CONFIG = {
  Applied:     { color: "#5B21B6", bg: "#EDE9FE", dot: "#7C3AED" },
  Shortlisted: { color: "#065F46", bg: "#D1FAE5", dot: "#10B981" },
  Interview:   { color: "#92400E", bg: "#FEF3C7", dot: "#F59E0B" },
  Accepted:    { color: "#1E3A5F", bg: "#DBEAFE", dot: "#3B82F6" },
  Rejected:    { color: "#991B1B", bg: "#FEE2E2", dot: "#EF4444" },
};

const PAYMENT_CONFIG = {
  Pending: { color: "#92400E", bg: "#FEF3C7" },
  Paid:    { color: "#065F46", bg: "#D1FAE5" },
  Failed:  { color: "#991B1B", bg: "#FEE2E2" },
};

const COURSE_GRADIENTS = {
  "fullstack-ai":   "linear-gradient(135deg,#7C3AED,#A78BFA)",
  "ai-ml":          "linear-gradient(135deg,#5B21B6,#8B5CF6)",
  "cyber-security": "linear-gradient(135deg,#4C1D95,#7C3AED)",
};

const COURSE_EMOJIS = {
  "fullstack-ai":   "💻",
  "ai-ml":          "🤖",
  "cyber-security": "🛡️",
};

const MyApplicationsScreen = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [applications, setApplications] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate("/login"); return; }
      setUser(u);
      try {
        const snap = await getDoc(doc(db, "internshipusers", u.uid));
        if (snap.exists()) setUserData(snap.data());
      } catch (e) { console.error(e); }
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      try {
        const q = query(collection(db, "internshipapplications"), where("userId", "==", user.uid));
        const snap = await getDocs(q);
        const apps = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        apps.sort((a, b) => {
          const tA = a.appliedAt?.toDate ? a.appliedAt.toDate() : new Date(a.appliedAt || 0);
          const tB = b.appliedAt?.toDate ? b.appliedAt.toDate() : new Date(b.appliedAt || 0);
          return tB - tA;
        });
        setApplications(apps);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, [user]);

  const handlePay = (app) => {
    navigate(`/dashboard/payment/${app.id}`);
  };

  const handleDownloadReceipt = async (app) => {
    try {
      // Fetch the latest successful payment order for this application
      const q = query(
        collection(db, "internshippaymentorder"),
        where("applicationId", "==", app.id),
        where("status", "==", "Success")
      );
      const snap = await getDocs(q);
      if (snap.empty) {
        showToast("No completed payment found for this application.", "error");
        return;
      }
      // Get the most recent successful record
      const records = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      records.sort((a, b) => {
        const tA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const tB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return tB - tA;
      });
      generateReceipt(records[0], user, userData);
    } catch (e) {
      console.error(e);
      showToast("Failed to generate receipt. Please try again.", "error");
    }
  };

  const showToast = (msg, type) => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#F5F3FF,#EDE9FE,#DDD6FE)" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", border: "4px solid #EDE9FE", borderTopColor: "#7C3AED", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
        <p style={{ color: "#7C3AED", fontWeight: 600, fontFamily: "Outfit,sans-serif" }}>Loading your applications...</p>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "linear-gradient(135deg,#F5F3FF 0%,#EDE9FE 50%,#F5F3FF 100%)", fontFamily: "Inter,sans-serif" }}>
      <style>{`
        @keyframes fadeInUp{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);}}
        @keyframes spin{to{transform:rotate(360deg);}}
        @keyframes slideIn{from{opacity:0;transform:translateX(40px);}to{opacity:1;transform:translateX(0);}}
        @keyframes pulse{0%,100%{opacity:1;}50%{opacity:0.5;}}
        ::-webkit-scrollbar{width:6px;} ::-webkit-scrollbar-thumb{background:rgba(139,92,246,0.3);border-radius:3px;}
        .app-card:hover{transform:translateY(-3px)!important;box-shadow:0 18px 50px rgba(109,40,217,0.15)!important;}
        .pay-btn:hover:not(:disabled){transform:translateY(-2px)!important;box-shadow:0 10px 28px rgba(109,40,217,0.4)!important;}
        .apps-main { padding: 32px 36px 48px 28px; }
        .apps-header-row { flex-direction: row; }
        @media (max-width: 768px) {
          .apps-main { padding: 72px 16px 32px 16px !important; }
          .apps-header-row { flex-direction: column !important; align-items: flex-start !important; }
          .app-card-inner { padding: 18px 16px !important; }
          .app-card-body { flex-direction: column !important; }
          .app-card-actions { flex-direction: column !important; }
          .app-card-actions button { width: 100% !important; justify-content: center !important; }
          .app-card-actions div { width: 100% !important; justify-content: center !important; }
        }
        @media (max-width: 480px) {
          .apps-main { padding: 68px 12px 28px 12px !important; }
        }
      `}</style>

      <Sidebar user={user} userData={userData} collapsed={collapsed} setCollapsed={setCollapsed} />

      <main className="apps-main" style={{ flex: 1, overflowY: "auto", minWidth: 0 }}>

        {toast && (
          <div style={{
            position: "fixed", top: 24, right: 28, zIndex: 9999,
            background: "rgba(255,255,255,0.97)",
            border: `2px solid ${toast.type === "success" ? "#A78BFA" : "#EF4444"}`,
            borderRadius: 16, padding: "14px 22px", boxShadow: "0 16px 48px rgba(109,40,217,0.18)",
            fontSize: 13, fontWeight: 600, color: "#4C1D95", animation: "slideIn 0.3s ease", maxWidth: 380,
          }}>
            {toast.type === "success" ? "💳 " : "❌ "}{toast.msg}
          </div>
        )}

        {/* Header */}
        <div style={{ marginBottom: 34, animation: "fadeInUp 0.4s ease both" }}>
          <div className="apps-header-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 52, height: 52, borderRadius: 16, background: "linear-gradient(135deg,#7C3AED,#A78BFA)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(124,58,237,0.32)" }}>
                <svg width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: 27, fontFamily: "Outfit,sans-serif", fontWeight: 800, color: "#4C1D95" }}>My Applications</h1>
                <p style={{ margin: 0, fontSize: 13, color: "#8B5CF6", fontWeight: 500, marginTop: 2 }}>Track and manage your internship applications</p>
              </div>
            </div>
            <button onClick={() => navigate("/dashboard/internships")} style={{
              padding: "12px 22px", borderRadius: 14, border: "none",
              background: "linear-gradient(135deg,#7C3AED,#A78BFA)", color: "#fff",
              fontSize: 13, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 6px 20px rgba(124,58,237,0.35)",
              display: "flex", alignItems: "center", gap: 8,
              transition: "transform 0.2s",
            }} onMouseEnter={e => e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e => e.currentTarget.style.transform="translateY(0)"}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Browse Internships
            </button>
          </div>

          {/* Summary chips */}
          {applications.length > 0 && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 20 }}>
              {[
                { label: "Total Applied", val: applications.length, color: "#7C3AED", bg: "#EDE9FE" },
                { label: "Pending Payment", val: applications.filter(a => a.paymentStatus === "Pending").length, color: "#92400E", bg: "#FEF3C7" },
                { label: "Paid", val: applications.filter(a => a.paymentStatus === "Paid").length, color: "#065F46", bg: "#D1FAE5" },
              ].map(c => (
                <div key={c.label} style={{ background: "rgba(255,255,255,0.82)", backdropFilter: "blur(12px)", border: "1.5px solid rgba(196,181,253,0.4)", borderRadius: 14, padding: "12px 20px", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: c.color }}>{c.val}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#5B21B6" }}>{c.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Empty state */}
        {applications.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 40px", background: "rgba(255,255,255,0.82)", backdropFilter: "blur(16px)", border: "1.5px solid rgba(196,181,253,0.4)", borderRadius: 24, textAlign: "center", animation: "fadeInUp 0.5s ease both" }}>
            <div style={{ fontSize: 72, marginBottom: 20 }}>📋</div>
            <h2 style={{ margin: "0 0 10px", fontSize: 22, fontFamily: "Outfit,sans-serif", fontWeight: 800, color: "#4C1D95" }}>No Applications Yet</h2>
            <p style={{ margin: "0 0 28px", fontSize: 14, color: "#8B5CF6", lineHeight: 1.6 }}>You haven't applied to any internships yet.<br/>Browse our programs and kickstart your career!</p>
            <button onClick={() => navigate("/dashboard/internships")} style={{ padding: "13px 32px", borderRadius: 14, border: "none", background: "linear-gradient(135deg,#7C3AED,#A78BFA)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 8px 24px rgba(124,58,237,0.35)" }}>
              Browse Internships →
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            {applications.map((app, idx) => {
              const st = STATUS_CONFIG[app.status] || STATUS_CONFIG["Applied"];
              const pt = PAYMENT_CONFIG[app.paymentStatus] || PAYMENT_CONFIG["Pending"];
              const gradient = COURSE_GRADIENTS[app.internshipId] || "linear-gradient(135deg,#7C3AED,#A78BFA)";
              const emoji = COURSE_EMOJIS[app.internshipId] || "🎓";
              const isPaying = payingId === app.id;

              return (
                <div key={app.id} className="app-card" style={{
                  borderRadius: 22, overflow: "hidden",
                  background: "rgba(255,255,255,0.90)", backdropFilter: "blur(20px)",
                  border: "1.5px solid rgba(196,181,253,0.45)",
                  boxShadow: "0 8px 36px rgba(109,40,217,0.09)",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  animation: `fadeInUp 0.5s ${0.1 * idx}s ease both`,
                }}>
                  <div style={{ height: 4, background: gradient }} />
                  <div className="app-card-inner" style={{ padding: "26px 30px" }}>
                    <div className="app-card-body" style={{ display: "flex", alignItems: "flex-start", gap: 18, flexWrap: "wrap" }}>
                      {/* Icon */}
                      <div style={{ width: 62, height: 62, borderRadius: 18, background: gradient, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0, boxShadow: "0 6px 18px rgba(109,40,217,0.25)" }}>
                        {emoji}
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
                          <h3 style={{ margin: 0, fontSize: 18, fontFamily: "Outfit,sans-serif", fontWeight: 800, color: "#4C1D95" }}>{app.internshipTitle}</h3>
                          <span style={{ background: st.bg, color: st.color, borderRadius: 8, fontSize: 11, fontWeight: 700, padding: "3px 11px", display: "flex", alignItems: "center", gap: 5 }}>
                            <div style={{ width: 6, height: 6, borderRadius: "50%", background: st.dot }} />
                            {app.status}
                          </span>
                          <span style={{ background: pt.bg, color: pt.color, borderRadius: 8, fontSize: 11, fontWeight: 700, padding: "3px 11px" }}>
                            💳 {app.paymentStatus}
                          </span>
                        </div>

                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
                          {[
                            { icon: "📂", label: app.internshipCategory },
                            { icon: "📅", label: app.duration },
                            app.stipend ? { icon: "💰", label: `₹${app.stipend}` } : null,
                            { icon: "🗓️", label: `Applied: ${formatDate(app.appliedAt)}` },
                          ].filter(Boolean).map((m, i) => (
                            <span key={i} style={{ fontSize: 12, color: "#6B7280", fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}>
                              <span>{m.icon}</span>{m.label}
                            </span>
                          ))}
                        </div>

                        {/* CTA */}
                        <div className="app-card-actions" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                            {app.paymentStatus !== "Paid" && (
                            <button className="pay-btn" onClick={() => handlePay(app)} disabled={isPaying} style={{
                              padding: "12px 28px", borderRadius: 13, border: "none",
                              background: isPaying ? "rgba(196,181,253,0.5)" : "linear-gradient(135deg,#7C3AED,#A78BFA)",
                              color: "#fff", fontSize: 14, fontWeight: 700,
                              cursor: isPaying ? "not-allowed" : "pointer",
                              boxShadow: isPaying ? "none" : "0 6px 20px rgba(124,58,237,0.35)",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              display: "flex", alignItems: "center", gap: 8,
                            }}>
                              {isPaying ? (
                                <><div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "spin 0.7s linear infinite" }} />Processing...</>
                              ) : (
                                <><span>💳</span> {app.paymentStatus?.includes("Paid") ? "Pay Next Installment" : "Pay Now"}</>
                              )}
                            </button>
                          )}
                          {(app.paymentStatus?.includes("Paid") || app.paymentStatus === "Paid") && (
                            <>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 20px", borderRadius: 13, background: "#D1FAE5", color: "#065F46", fontSize: 13, fontWeight: 700 }}>
                                ✅ {app.paymentStatus === "Paid" ? "Payment Completed" : app.paymentStatus}
                              </div>
                              <button onClick={() => handleDownloadReceipt(app)} style={{
                                padding: "12px 22px", borderRadius: 13, border: "1.5px solid rgba(16,185,129,0.5)",
                                background: "rgba(209,250,229,0.3)", color: "#065F46", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 6
                              }} onMouseEnter={e => e.currentTarget.style.background="rgba(209,250,229,0.8)"} onMouseLeave={e => e.currentTarget.style.background="rgba(209,250,229,0.3)"}>
                                🧾 Download Receipt
                              </button>
                            </>
                          )}
                          <button onClick={() => navigate("/dashboard/internships")} style={{
                            padding: "12px 22px", borderRadius: 13, border: "1.5px solid rgba(167,139,250,0.6)",
                            background: "transparent", color: "#7C3AED", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "background 0.2s",
                          }} onMouseEnter={e => e.currentTarget.style.background="rgba(237,233,254,0.8)"} onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                            View More Internships
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyApplicationsScreen;
